import { Link } from '@tanstack/react-router'

import { type HomeWidgetProps } from '@/lib/home-widgets'
import { cn } from '@/lib/utils'
import { HomeWidgetEmpty, HomeWidgetError, HomeWidgetLoading } from '@/ui/home-widget'
import { RelativeTime } from '@/ui/relative-time'

import { useAgentHealth } from './agent-health-queries'

export function AgentHealthWidget({ entityId }: HomeWidgetProps) {
  const health = useAgentHealth(entityId)

  if (!health.data) {
    return health.isError ? <HomeWidgetError onRetry={() => void health.refetch()} /> : <HomeWidgetLoading />
  }

  const { online, offline, longestOffline } = health.data
  if (online + offline === 0) return <HomeWidgetEmpty>No agents at this entity or below.</HomeWidgetEmpty>

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3">
        <div>
          <dt className="text-muted-foreground text-sm">Online</dt>
          <dd className="text-2xl font-semibold tabular-nums">{online}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-sm">Offline</dt>
          <dd className={cn('text-2xl font-semibold tabular-nums', offline > 0 && 'text-destructive')}>{offline}</dd>
        </div>
      </dl>
      <div aria-hidden="true" className="bg-muted flex h-2 overflow-hidden rounded-full">
        <div data-slot="agent-health-online" className="bg-success" style={{ flexGrow: online }} />
        <div data-slot="agent-health-offline" className="bg-destructive" style={{ flexGrow: offline }} />
      </div>
      {offline === 0
        ? <p className="text-muted-foreground">Every agent is reporting.</p>
        : (
            <div className="space-y-2">
              <h3 className="text-muted-foreground text-sm">Offline longest</h3>
              <ul className="space-y-2">
                {longestOffline.map((agent) => (
                  <li key={agent.key} className="flex min-w-0 items-baseline justify-between gap-2">
                    <Link
                      to="/$entityId/agents/$agentId"
                      params={{ entityId: agent.values.entityId ?? entityId, agentId: agent.key }}
                      title={agent.values.name ?? agent.key}
                      className="truncate font-medium underline-offset-4 hover:underline"
                    >
                      {agent.values.name ?? agent.key}
                    </Link>
                    {agent.values.lastOnlineAt && (
                      <span className="text-muted-foreground shrink-0 text-sm">
                        Last seen <RelativeTime iso={agent.values.lastOnlineAt} />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
    </div>
  )
}
